import type {ImportRecord, ImportStatus} from '../../core/importModels'

/**
 * `import.status` notifications from the storage import worker (omicscloud-cdk
 * import-status-store.ts → EventsBus → the notification WebSocket). Sent when an import
 * starts and when it ends.
 */
export const IMPORT_STATUS_MESSAGE_TYPE = 'import.status'

export interface ImportStatusMessage {
  importId: string
  status: ImportStatus
  destinationPath: string
  copiedFiles: number
  failedFiles: number
}

const STATUSES: ReadonlySet<string> = new Set(['queued', 'running', 'succeeded', 'partial', 'failed'])

/** Normalize a raw notification-socket message, or null if it isn't a valid import.status. */
export function parseImportStatusMessage(message: unknown): ImportStatusMessage | null {
  if (!message || typeof message !== 'object') return null
  const m = message as Record<string, unknown>
  if (m.type !== IMPORT_STATUS_MESSAGE_TYPE) return null
  if (typeof m.importId !== 'string' || !m.importId) return null
  if (typeof m.status !== 'string' || !STATUSES.has(m.status)) return null
  return {
    importId: m.importId,
    status: m.status as ImportStatus,
    destinationPath: typeof m.destinationPath === 'string' ? m.destinationPath : '',
    copiedFiles: Number(m.copiedFiles) || 0,
    failedFiles: Number(m.failedFiles) || 0,
  }
}

export function isImportActive(status: ImportStatus): boolean {
  return status === 'queued' || status === 'running'
}

/** '/' for the root, '/a/b' otherwise. */
export function displayDestination(destinationPath: string): string {
  return `/${destinationPath}`
}

export const IMPORT_STATUS_LABEL: Record<ImportStatus, string> = {
  queued: 'Queued',
  running: 'Importing',
  succeeded: 'Done',
  partial: 'Partly failed',
  failed: 'Failed',
}

/** "3 of 5 files" while running / once known, "3 files" when the total isn't known yet. */
export function importProgressText(record: Pick<ImportRecord, 'copiedFiles' | 'failedFiles' | 'totalFiles'>): string {
  const done = record.copiedFiles
  const plural = (n: number) => (n === 1 ? 'file' : 'files')
  const copied =
    record.totalFiles !== undefined
      ? `${done} of ${record.totalFiles} ${plural(record.totalFiles)}`
      : `${done} ${plural(done)}`
  return record.failedFiles > 0 ? `${copied}, ${record.failedFiles} failed` : copied
}

/**
 * (import, status) pairs this tab already announced. The notification bus replays messages
 * spooled while the socket was down, so the same end can arrive more than once.
 */
const announcedImportStatuses = new Set<string>()

/** True the first time this tab sees the import end with this status. */
export function markImportStatusAnnounced(message: Pick<ImportStatusMessage, 'importId' | 'status'>): boolean {
  const key = `${message.importId}:${message.status}`
  if (announcedImportStatuses.has(key)) return false
  announcedImportStatuses.add(key)
  return true
}

/** Test hook: forget what was announced. */
export function resetAnnouncedImportStatuses(): void {
  announcedImportStatuses.clear()
}

/** The toast shown when an import ends; null while it is still going. */
export function importFinishedToast(
  message: ImportStatusMessage
): {type: 'success' | 'warning' | 'error'; text: string} | null {
  const where = displayDestination(message.destinationPath)
  const files = (n: number) => `${n} ${n === 1 ? 'file' : 'files'}`
  switch (message.status) {
    case 'succeeded':
      return {type: 'success', text: `Import finished: ${files(message.copiedFiles)} copied to ${where}.`}
    case 'partial':
      return {
        type: 'warning',
        text: `Import finished with errors: ${files(message.copiedFiles)} copied to ${where}, ${message.failedFiles} failed. See Imports for details.`,
      }
    case 'failed':
      return {type: 'error', text: `Import to ${where} failed. See Imports for details.`}
    default:
      return null
  }
}
